import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  Add,
  Clear,
  Delete,
  Description,
  Edit,
  Folder,
  MoreVert,
} from '@mui/icons-material';

import DropdownMenu from 'generic/components/ui/DropdownMenu';
import Corpus from 'generic/components/pages/Corpus';

const Collections = ({
  collections,
  idUser,
  inputSearch,
  hasCollectionOwner,
  lastestActivedCorpora,
  totalCollectionsLength,
  handleActivateCollection,
  handleChangeInputSearch,
  handleOpenCollectionDialog,
  handleOpenDeleteCollectionConfirmDialog,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        width: '100%',
        p: 3,
      }}
    >
      {(
        !_.isEmpty(collections)
        && (_.some(collections, { utilisateur: idUser })
        || _.some(collections, (collection) => _.some(collection.corpora, (corpus) => !_.isEmpty(corpus))))
      ) || !_.isEmpty(inputSearch)
        ? (
          <Fragment>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Champ de recherche */}
              <TextField
                label={t('collections.search')}
                value={inputSearch}
                onChange={(event) => handleChangeInputSearch(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="text.neutral" sx={{ mr: 1 }}>
                        {`${_.flatMap(collections, 'corpora').length}/${totalCollectionsLength}`}
                      </Typography>
                      <IconButton disabled={_.isEmpty(inputSearch)} onClick={() => handleChangeInputSearch('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* Bouton pour créer une nouvelle collection ou un nouveau corpus */}
              <DropdownMenu
                btnTag="button"
                btnProps={{
                  endIcon: <Add fontSize="small" />,
                  size: 'medium',
                }}
                btnChildren={t('ui.new')}
              >
                <MenuItem onClick={() => handleOpenCollectionDialog('collection', 'create')}>
                  <Folder fontSize="small" color="primary" sx={{ mr: 1 }} />
                  {t('collections.collection.create')}
                </MenuItem>
                <Tooltip title={(!hasCollectionOwner) ? t('collections.cannotAddCorpus') : ''} placement="right">
                  <Box>
                    <MenuItem
                      disabled={!hasCollectionOwner}
                      onClick={() => handleOpenCollectionDialog('corpus', 'create')}
                    >
                      <Description fontSize="small" color="primary" sx={{ mr: 1 }} />
                      {t('collections.corpus.create')}
                    </MenuItem>
                  </Box>
                </Tooltip>
              </DropdownMenu>
            </Box>
            {/* Dernier corpus activé */}
            {!_.isEmpty(lastestActivedCorpora) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6">{t('collections.lastest_activated')}</Typography>
                <Grid container spacing={3}>
                  {_.map(
                    lastestActivedCorpora,
                    (corpus) => (
                      <Grid key={corpus.collection} item sm={12} md={6} lg={3}>
                        <Corpus
                          corpus={corpus}
                          isOwner={corpus.utilisateur === idUser}
                          variant="light"
                          handleActivateCollection={handleActivateCollection}
                          handleOpenCollectionDialog={handleOpenCollectionDialog}
                          handleOpenDeleteCollectionConfirmDialog={handleOpenDeleteCollectionConfirmDialog}
                        />
                      </Grid>
                    ),
                  )}
                </Grid>
              </Box>
            )}
            {/* Toutes les collections */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pb: 3,
              }}
            >
              <Typography variant="h6">{t('collections.my_collections')}</Typography>
              {_.isEmpty(collections) ? (
                <Box sx={{ textAlign: 'center' }}>{t('collections.no_search_results')}</Box>
              ) : (
                <Grid container spacing={3}>
                  {_.map(
                    collections,
                    (collection) => {
                      let cannotAddMessage = '';
                      let cannotDeleteMessage = '';
                      if (collection.utilisateur !== idUser) {
                        cannotAddMessage = t('collections.cannotAddNotOwner');
                        cannotDeleteMessage = t('collections.cannotDeleteNotOwner');
                      } else if (!_.isEmpty(collection.corpora)) {
                        cannotDeleteMessage = t('collections.cannotDeleteHasCorpus');
                      }
                      return (!_.isEmpty(collection.corpora) || collection.utilisateur === idUser) && (
                        <Grid key={collection.collection} item sm={12} md={6} lg={3}>
                          <Card
                            sx={{
                              height: 350,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                              p: 2,
                              backgroundColor: 'action.hover',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Folder color="primary" />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {collection.libelle}
                                </Typography>
                              </Box>
                              <DropdownMenu
                                btnChildren={<MoreVert />}
                                btnProps={{
                                  sx: { padding: '2px' },
                                }}
                              >
                                <MenuItem
                                  onClick={() => handleOpenCollectionDialog(
                                    'collection',
                                    'edit',
                                    {
                                      id: collection.collection,
                                      owner: collection.utilisateur_createur,
                                      visibility: collection.visibilite_collection,
                                      collaborators: collection.collaborators,
                                      libelle: collection.libelle,
                                      description: collection.description,
                                    },
                                  )}
                                >
                                  <Edit fontSize="small" color="primary" sx={{ mr: 1 }} />
                                  {collection.utilisateur !== idUser
                                    ? t('collections.collection.params') : t('collections.collection.edit')}
                                </MenuItem>
                                <Tooltip
                                  title={cannotAddMessage}
                                  placement="right"
                                >
                                  <Box>
                                    <MenuItem
                                      disabled={collection.utilisateur !== idUser}
                                      onClick={() => handleOpenCollectionDialog(
                                        'corpus',
                                        'create',
                                        {
                                          collection_parente: collection.collection,
                                          owner: collection.utilisateur_createur,
                                        },
                                      )}
                                    >
                                      <Description fontSize="small" color="primary" sx={{ mr: 1 }} />
                                      {t('collections.corpus.add')}
                                    </MenuItem>
                                  </Box>
                                </Tooltip>
                                <Tooltip
                                  title={cannotDeleteMessage}
                                  placement="right"
                                >
                                  <Box>
                                    <MenuItem
                                      disabled={collection.utilisateur !== idUser || !_.isEmpty(collection.corpora)}
                                      sx={{ color: 'error.main' }}
                                      onClick={
                                        () => handleOpenDeleteCollectionConfirmDialog(
                                          'collection',
                                          [collection.collection],
                                        )
                                      }
                                    >
                                      <Delete fontSize="small" color="error" sx={{ mr: 1 }} />
                                      {t('collections.collection.delete')}
                                    </MenuItem>
                                  </Box>
                                </Tooltip>
                              </DropdownMenu>
                            </Box>
                            {_.isEmpty(collection.corpora) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: 1,
                                  height: '100%',
                                  color: 'primary.main',
                                  border: '2px dashed',
                                  borderColor: 'primary.main',
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleOpenCollectionDialog(
                                  'corpus',
                                  'create',
                                  {
                                    collection_parente: collection.collection,
                                    owner: collection.utilisateur_createur,
                                  },
                                )}
                              >
                                <Add fontSize="medium" />
                                {t('collections.corpus.add')}
                              </Box>
                            ) : (
                              <Box sx={{ overflow: 'auto' }}>
                                {_.map(
                                  _.sortBy(collection.corpora, (corpus) => corpus.libelle.toLowerCase()),
                                  (corpus) => (
                                    <Corpus
                                      corpus={corpus}
                                      key={corpus.collection}
                                      isOwner={corpus.utilisateur === idUser}
                                      handleActivateCollection={handleActivateCollection}
                                      handleOpenCollectionDialog={handleOpenCollectionDialog}
                                      handleOpenDeleteCollectionConfirmDialog={handleOpenDeleteCollectionConfirmDialog}
                                    />
                                  ),
                                )}
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      );
                    },
                  )}
                </Grid>
              )}
            </Box>
          </Fragment>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Folder color="primary" fontSize="large" />
            {t('collections.no_results')}
            <Button
              color="primary"
              startIcon={<Add />}
              sx={{ mt: 1 }}
              onClick={() => handleOpenCollectionDialog('collection', 'create')}
            >
              {t('collections.collection.create')}
            </Button>
          </Box>
        )}
    </Box>
  );
};

Collections.propTypes = {
  collections: PropTypes.arrayOf(PropTypes.shape({
    active: PropTypes.bool,
    collection: PropTypes.number,
    collection_parente: PropTypes.number,
    description: PropTypes.string,
    image: PropTypes.string,
    libelle: PropTypes.string,
    utilisateur: PropTypes.number,
  })).isRequired,
  hasCollectionOwner: PropTypes.bool.isRequired,
  idUser: PropTypes.number.isRequired,
  inputSearch: PropTypes.string.isRequired,
  lastestActivedCorpora: PropTypes.arrayOf(PropTypes.shape({
    active: PropTypes.bool,
    collection: PropTypes.number,
    collection_parente: PropTypes.number,
    collection_parente_name: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    libelle: PropTypes.string,
    utilisateur: PropTypes.number,
  })).isRequired,
  totalCollectionsLength: PropTypes.number.isRequired,
  handleActivateCollection: PropTypes.func.isRequired,
  handleChangeInputSearch: PropTypes.func.isRequired,
  handleOpenCollectionDialog: PropTypes.func.isRequired,
  handleOpenDeleteCollectionConfirmDialog: PropTypes.func.isRequired,
};

export default Collections;
